/**
 * §11.3 Security Overview — 6 pillars, tone-tinted grid.
 */

import React from 'react'
import clsx from 'clsx'
import {
  ShieldCheck, Lock, Layers, KeyRound, Activity, Siren, Sparkles,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { SECURITY_PILLARS } from '../data'
import { TONE } from '../_shared'

const ICONS = { ShieldCheck, Lock, Layers, KeyRound, Activity, Siren, Sparkles }

export default function SecurityOverview() {
  return (
    <section id="security" className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Security</Eyebrow>
          <SectionHeading align="center"
            title={<>Six pillars that{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">protect every tenant.</span></>}
            subtitle="Security is designed into the platform fabric — not bolted onto it.  Every product surface inherits the same controls, isolation and audit posture by default."
          />
        </div>
        <div className="grid gap-3 md:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {SECURITY_PILLARS.map((p) => {
            const tone = TONE[p.tone] || TONE.orange
            const Icon = ICONS[p.iconKey] || ShieldCheck
            return (
              <div key={p.id} className={clsx(
                'relative rounded-2xl border border-white/10 bg-white/[0.02] p-6 backdrop-blur-xl',
                'transition-all hover:border-white/20 hover:bg-white/[0.03] hover:-translate-y-[1px] overflow-hidden',
              )}>
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 40% at 20% 0%, rgba(${tone.rgb},0.10), transparent 70%)` }} />
                <div className="relative flex flex-col gap-4">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <Icon strokeWidth={1.8} style={{ width: 20, height: 20 }} />
                  </span>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{p.label}</h3>
                    <p className="text-sm text-zinc-400 leading-relaxed mt-2">{p.body}</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
